import { Header, Table, flexRender } from '@tanstack/react-table';
import { DragEventHandler, createRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  BoxProps,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverAnchor,
  PopoverContent,
  Portal,
} from '@chakra-ui/react';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { PROPERTY_DRAG_DATA_FORMAT } from '../../utils/property-dragging-utils';
import SparkelIcon from '../common/icon/SparkelIcon';
import { ColumnDropConfirmationForm } from '../project/order-entries-panel/ColumnDropConfirmationForm';
import {
  HeaderText,
  SortableColumnMenuItems,
  SparkelTableColumnMeta,
  TableEntry,
} from './Table';
import { flexProps, getColumnName } from './table-utils';
import { Resizer } from './Resizer';

// eslint-disable-next-line complexity
export function TableHeader<D extends TableEntry>({
  header,
  onColumnRename,
  onColumnDrop,
  activeDropColumnData,
  closeColumnDropConfirmation,
  backgroundColor,
  textColor,
  borderColor,
  hoverColor,
  tableInstance,
  onConfirmColumnDrop,
  existingColumnNames,
}: {
  header: Header<D, unknown>;
  onColumnRename: (columnId: string, name: string) => void;
  onColumnDrop: ((columnId: string) => DragEventHandler) | undefined;
  activeDropColumnData: {
    columnId: string;
    property: {
      category: string;
      propertyName: string;
      displayName: string;
    };
  } | null;
  closeColumnDropConfirmation: () => void;
  backgroundColor: string;
  textColor: string;
  borderColor: string;
  hoverColor: string;
  tableInstance: Table<D>;
  onConfirmColumnDrop: (isSum: boolean) => void;
  existingColumnNames: string[];
}) {
  const { register, handleSubmit } = useForm<{
    name: string;
  }>();

  const { t } = useTranslation('table');

  const [isEditing, setIsEditing] = useState(false);
  const columnMeta = (header.column.columnDef.meta ??
    {}) as SparkelTableColumnMeta;
  const hasMenu = header.column.getCanSort() || columnMeta.menuItems;

  const canDrop = typeof onColumnDrop === 'function' && columnMeta.canDrop;

  let dragProps: Pick<
    BoxProps,
    'onDragOver' | 'onDragEnter' | 'onDragLeave' | 'onDrop'
  > | null = null;

  if (canDrop) {
    dragProps = {
      onDragOver: (event) => {
        if (event.dataTransfer.types.includes(PROPERTY_DRAG_DATA_FORMAT)) {
          event.currentTarget.setAttribute('data-droptarget', 'true');
          event.preventDefault();
        }
      },
      onDragEnter: (event) => {
        if (event.dataTransfer.types.includes(PROPERTY_DRAG_DATA_FORMAT)) {
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
        }
      },
      onDragLeave: (event) => {
        event.currentTarget.removeAttribute('data-droptarget');
      },
      onDrop: (event) => {
        event.currentTarget.removeAttribute('data-droptarget');
        onColumnDrop(header.column.id)(event);
      },
    };
  }

  const handleNameSubmit = handleSubmit(async (data) => {
    await onColumnRename(header.column.id, data.name);
    setIsEditing(false);
  });

  const popoverInitialFocusRef = createRef<HTMLElement>();
  const headerName =
    typeof header.column.columnDef.header === 'string'
      ? header.column.columnDef.header
      : '';

  return (
    <Popover
      key={header.id}
      placement="bottom"
      isOpen={activeDropColumnData?.columnId === header.id}
      onClose={closeColumnDropConfirmation}
      closeOnBlur={false}
      closeOnEsc={true}
      initialFocusRef={popoverInitialFocusRef}
      isLazy
    >
      <PopoverAnchor>
        <Box
          role="columnheader"
          {...flexProps(header.column)}
          position={'relative'}
          backgroundColor={backgroundColor}
          height="100%"
          title={getColumnName(header.column)}
          textColor={textColor}
          borderBottomWidth="1px"
          borderBottomColor={borderColor}
          lineHeight={6}
          paddingY={1}
          paddingX={1}
          fontSize={'sm'}
          {...dragProps}
          _hover={{
            backgroundColor:
              activeDropColumnData === null &&
              (hasMenu || header.column.id === 'addColumn')
                ? hoverColor
                : undefined,
          }}
        >
          {hasMenu ? (
            // If this Menu is lazy, the delete modal will not be able to open
            <Menu>
              {isEditing ? (
                <form onSubmit={handleNameSubmit}>
                  <Input
                    {...register('name', { required: true })}
                    variant={'unstyled'}
                    aria-label="Rename column"
                    size="sm"
                    autoFocus
                    onBlur={() => {
                      setIsEditing(false);
                    }}
                    textAlign={
                      (header.column.columnDef.meta as SparkelTableColumnMeta)
                        ?.isNumeric
                        ? 'end'
                        : undefined
                    }
                    defaultValue={headerName}
                  />
                </form>
              ) : (
                <MenuButton
                  w={'100%'}
                  disabled={activeDropColumnData?.columnId === header.column.id}
                  onDoubleClick={() => setIsEditing(true)}
                  fontSize="xs"
                >
                  <HeaderText isNumeric={columnMeta.isNumeric}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </HeaderText>
                </MenuButton>
              )}
              <Portal>
                <MenuList fontSize={'sm'} zIndex={99}>
                  {header.column.getCanSort() ? (
                    <SortableColumnMenuItems column={header.column} />
                  ) : null}
                  {typeof columnMeta.menuItems === 'function'
                    ? columnMeta.menuItems(tableInstance)
                    : columnMeta.menuItems}
                  <MenuItem
                    onClick={() => setIsEditing(true)}
                    icon={<SparkelIcon icon={faPen} fixedWidth />}
                  >
                    {t('table:column.rename')}
                  </MenuItem>
                </MenuList>
              </Portal>
            </Menu>
          ) : (
            <HeaderText isNumeric={columnMeta.isNumeric}>
              {flexRender(header.column.columnDef.header, header.getContext())}
            </HeaderText>
          )}
          {header.column.getCanResize() ? <Resizer header={header} /> : null}
        </Box>
      </PopoverAnchor>
      <Portal>
        <PopoverContent width="2xs">
          <ColumnDropConfirmationForm
            columnId={header.id}
            property={activeDropColumnData?.property}
            onConfirm={onConfirmColumnDrop}
            existingColumnNames={existingColumnNames}
            ref={popoverInitialFocusRef}
          />
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
